import React from 'react';
import { IComplianceResult } from '../types';
import { Link } from 'react-router-dom';

interface ComplianceCardProps {
  compliance: IComplianceResult;
  showCycleLink?: boolean;
}

// Map profession name → brand color token
function professionColor(name: string): string {
  const n = (name || '').toLowerCase();
  if (n.includes('arch'))                               return 'var(--color-prof-arch)';
  if (n.includes('engineer') || n.includes(' pe ') || n === 'pe') return 'var(--color-prof-pe)';
  if (n.includes('cpa') || n.includes('account'))       return 'var(--color-prof-cpa)';
  if (n.includes('law') || n.includes('attorney'))      return 'var(--color-prof-law)';
  return 'var(--color-accent)';
}

// Urgency color by days remaining
function urgencyColor(days: number, isMet: boolean): string {
  if (isMet)      return 'var(--color-success)';
  if (days < 0)   return 'var(--color-danger)';
  if (days < 30)  return 'var(--color-danger)';
  if (days < 60)  return 'var(--color-warning)';
  return 'var(--color-success)';
}

// Ring stroke color based on completion
function ringColor(pct: number, isMet: boolean): string {
  if (isMet)    return 'var(--color-success)';
  if (pct >= 75) return 'var(--color-accent)';
  if (pct >= 50) return 'var(--color-accent-warm)';
  return 'var(--color-danger)';
}

const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ComplianceCard: React.FC<ComplianceCardProps> = ({ compliance, showCycleLink = true }) => {
  const {
    cycleId,
    cycleName,
    professionName,
    stateName,
    totalEarned,
    totalRequired,
    percentComplete,
    isMet,
    categories,
    warnings,
    daysRemaining,
    cycleStartDate,
    cycleEndDate,
  } = compliance;

  const pct           = Math.min(Math.max(percentComplete || 0, 0), 100);
  const strokeOffset  = RING_CIRCUMFERENCE - (pct / 100) * RING_CIRCUMFERENCE;
  const profColor     = professionColor(professionName);
  const deadlineColor = urgencyColor(daysRemaining, isMet);
  const strokeFill    = ringColor(pct, isMet);

  const formatDate = (d?: string) => {
    if (!d) return '';
    const [year, month, day] = d.split('T')[0].split('-');
    return `${month}/${day}/${year}`;
  };

  const formatDays = () => {
    if (daysRemaining < 0) return 'Expired';
    if (daysRemaining === 0) return 'Due today';
    if (daysRemaining === 1) return '1 day left';
    return `${daysRemaining} days left`;
  };

  const statusLabel = isMet
    ? 'Compliant'
    : daysRemaining < 0
      ? 'Expired'
      : daysRemaining < 30
        ? 'Urgent'
        : daysRemaining < 60
          ? 'Due Soon'
          : 'In Progress';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border)',
        borderLeft: `4px solid ${profColor}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
        height: '100%',
        transition: 'box-shadow var(--transition-base)',
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = 'var(--shadow-card)')}
    >
      {/* Header */}
      <div style={{
        padding: '1rem 1rem 0.75rem',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.5rem',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {showCycleLink ? (
            <Link
              to={`/cycles/${cycleId}`}
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 'var(--fw-bold)',
                fontSize: 'var(--fs-md)',
                color: 'var(--color-text)',
                textDecoration: 'none',
                display: 'block',
                lineHeight: 1.25,
              }}
              onMouseEnter={e => (e.currentTarget.style.color = profColor)}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text)')}
            >
              {cycleName}
            </Link>
          ) : (
            <span style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 'var(--fw-bold)',
              fontSize: 'var(--fs-md)',
              color: 'var(--color-text)',
              lineHeight: 1.25,
              display: 'block',
            }}>
              {cycleName}
            </span>
          )}
          <div style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--color-text-muted)',
            marginTop: '0.25rem',
            fontWeight: 'var(--fw-medium)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            {professionName} · {stateName}
          </div>
          {cycleStartDate && cycleEndDate && (
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--color-text-muted)',
              marginTop: '0.125rem',
            }}>
              {formatDate(cycleStartDate)} – {formatDate(cycleEndDate)}
            </div>
          )}
        </div>

        {/* Status badge */}
        <span style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 'var(--fw-semibold)',
          fontSize: 'var(--fs-xs)',
          padding: '0.3rem 0.65rem',
          borderRadius: 'var(--radius-full)',
          background: isMet
            ? 'var(--color-success-light)'
            : daysRemaining < 30
              ? 'var(--color-danger-light)'
              : daysRemaining < 60
                ? 'var(--color-warning-light)'
                : 'var(--color-surface-muted)',
          color: isMet
            ? 'var(--color-success)'
            : daysRemaining < 30
              ? 'var(--color-danger)'
              : daysRemaining < 60
                ? 'var(--color-accent-warm-hover)'
                : 'var(--color-text-muted)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          {statusLabel}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: '1rem', flex: 1 }}>
        {/* Circular ring + stats row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          {/* SVG Ring */}
          <div style={{ flexShrink: 0 }}>
            <svg width="100" height="100" viewBox="0 0 100 100" role="img" aria-label={`${pct.toFixed(0)}% complete`}>
              {/* Track */}
              <circle
                cx="50" cy="50" r={RING_RADIUS}
                fill="none"
                stroke="var(--color-surface-muted)"
                strokeWidth="8"
              />
              {/* Progress arc */}
              <circle
                cx="50" cy="50" r={RING_RADIUS}
                fill="none"
                stroke={strokeFill}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={strokeOffset}
                transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
              {/* Center text */}
              <text
                x="50" y="46"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '18px',
                  fontWeight: 800,
                  fill: 'var(--color-text)',
                }}
              >
                {pct.toFixed(0)}%
              </text>
              <text
                x="50" y="63"
                textAnchor="middle"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '9px',
                  fontWeight: 500,
                  fill: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                complete
              </text>
            </svg>
          </div>

          {/* Credit numbers + deadline */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--fs-3xl)',
              fontWeight: 'var(--fw-black)',
              color: 'var(--color-text)',
              lineHeight: 1,
            }}>
              {totalEarned}
              <span style={{
                fontSize: 'var(--fs-md)',
                fontWeight: 'var(--fw-regular)',
                color: 'var(--color-text-muted)',
                marginLeft: '0.25rem',
              }}>
                / {totalRequired}
              </span>
            </div>
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--color-text-muted)',
              marginTop: '0.2rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 'var(--fw-medium)',
            }}>
              credits earned
            </div>

            {/* Deadline pill */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              marginTop: '0.6rem',
              padding: '0.25rem 0.6rem',
              borderRadius: 'var(--radius-full)',
              background: isMet ? 'var(--color-success-light)' : `color-mix(in srgb, ${deadlineColor} 12%, transparent)`,
              border: `1px solid ${deadlineColor}`,
            }}>
              <span style={{
                width: '6px', height: '6px',
                borderRadius: '50%',
                background: deadlineColor,
                flexShrink: 0,
              }} />
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-semibold)',
                color: deadlineColor,
              }}>
                {formatDays()}
              </span>
            </div>
          </div>
        </div>

        {/* Category breakdown — only show rows where there's a minimum to meet OR credits earned */}
        {categories && categories.filter(c => c.required > 0 || c.earned > 0).length > 0 && (
          <div>
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '0.5rem',
            }}>
              Category Requirements
            </div>
            {categories.filter(c => c.required > 0 || c.earned > 0).map((cat, idx) => {
              const barPct = cat.required > 0
                ? Math.min((cat.earned / cat.required) * 100, 100)
                : 100;
              const isOverLimit = cat.categoryTag === 'SELF_STUDY_MAX' && cat.earned > cat.required;
              return (
              <div key={idx} style={{ marginBottom: '0.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.2rem',
                }}>
                  <span style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--fs-xs)',
                    color: cat.isMet ? 'var(--color-success)' : 'var(--color-text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}>
                    <span style={{ fontSize: '10px' }}>{cat.isMet ? '✓' : '○'}</span>
                    {cat.categoryName}
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 'var(--fw-semibold)',
                    color: isOverLimit ? 'var(--color-danger)' : cat.isMet ? 'var(--color-success)' : 'var(--color-danger)',
                  }}>
                    {cat.earned} / {cat.required}
                  </span>
                </div>
                {/* Thin progress bar */}
                <div style={{
                  height: '4px',
                  background: 'var(--color-surface-muted)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${barPct}%`,
                    background: isOverLimit ? 'var(--color-danger)' : cat.isMet ? 'var(--color-success)' : 'var(--color-accent-warm)',
                    borderRadius: 'var(--radius-full)',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.6rem 0.75rem',
            background: 'var(--color-warning-light)',
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-sm)',
          }}>
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--color-accent-warm-hover)',
              marginBottom: '0.25rem',
            }}>
              Attention
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {warnings.map((w, idx) => (
                <li key={idx} style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--color-accent-warm-hover)',
                  marginBottom: '0.1rem',
                }}>
                  {w}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceCard;
